import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../lib/content";

const links = [
  { id: "nosotros", label: "Nosotros" },
  { id: "mision-vision", label: "Misión & Visión" },
  { id: "servicios", label: "Servicios" },
  { id: "distintivos", label: "Distintivos" },
  { id: "contacto", label: "Contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FCFAFF]/80 border-b border-[var(--ms-border)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 h-20 flex items-center justify-between">
        <a
          href="#hero"
          data-testid="navbar-logo"
          onClick={(e) => { e.preventDefault(); handleClick("hero"); }}
          className="flex items-center gap-2 group"
        >
          <img
            src={LOGO_URL}
            alt="Marketing Sublime"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </a>

        <ul className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`nav-link-${l.id}`}
                onClick={() => handleClick(l.id)}
                className={`font-body text-[14px] tracking-wide transition-colors duration-300 relative after:content-[''] after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 hover:after:w-full after:transition-all after:duration-300 ${
                  scrolled
                    ? "text-[var(--ms-ink)]/75 hover:text-[var(--ms-purple)] after:bg-[var(--ms-purple)]"
                    : "text-white/85 hover:text-white after:bg-white"
                }`}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <button
            data-testid="navbar-cta"
            onClick={() => handleClick("contacto")}
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium tracking-wide transition-all duration-400 hover:scale-[1.03] ${
              scrolled
                ? "bg-[var(--ms-ink)] text-white hover:bg-[var(--ms-purple)]"
                : "bg-white text-[var(--ms-ink)] hover:bg-[var(--ms-lila)] hover:text-white"
            }`}
          >
            Hablemos
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--ms-lila)]" />
          </button>
          <button
            data-testid="navbar-mobile-toggle"
            className={`lg:hidden p-2 rounded-full transition ${
              scrolled ? "hover:bg-[var(--ms-purple)]/10 text-[var(--ms-ink)]" : "hover:bg-white/10 text-white"
            }`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-[#FCFAFF] border-t border-[var(--ms-border)]"
        >
          <ul className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => handleClick(l.id)}
                  className="w-full text-left font-display text-2xl tracking-tight text-[var(--ms-ink)] hover:text-[var(--ms-purple)] transition py-2"
                >
                  {l.label}
                </button>
              </li>
            ))}
            <li>
              <button
                data-testid="navbar-mobile-cta"
                onClick={() => handleClick("contacto")}
                className="mt-2 w-full text-center px-5 py-3 rounded-full bg-[var(--ms-purple)] text-white font-medium"
              >
                Hablemos
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
